/**
 * 剧本配置块管理页面
 * 提供剧本配置的在线编辑功能，支持 NPC、任务、物品等多种配置类型的增删改查
 */
import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Tabs,
  Typography,
  Tooltip,
  Badge,
  Empty,
  Statistic,
  Drawer,
  Alert,
  Steps
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  OrderedListOutlined,
  TeamOutlined,
  AimOutlined,
  ShoppingCartOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  FlagOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  BellOutlined,
  MessageOutlined,
  RobotOutlined,
  PictureOutlined,
  FileTextOutlined,
  CloseOutlined,
  FullscreenOutlined,
  CodeOutlined,
  FormOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { configBlockApi, scriptApi, scriptPackageApi } from '@/services/api'
import type { ScriptConfigBlock, ConfigType } from '@/types/scriptConfigBlock'
import { CONFIG_TYPE_LABELS, ConfigBlockStatsResponse } from '@/types/scriptConfigBlock'
import { validateConfigData, getValidationErrorSummary } from '@/utils/schemaValidator'
import NpcEditorForm from './components/NpcEditorForm'
import QuestEditorForm from './components/QuestEditorForm'
import ItemEditorForm from './components/ItemEditorForm'
import VariableEditorForm from './components/VariableEditorForm'
import FactionEditorForm from './components/FactionEditorForm'
import LocationEditorForm from './components/LocationEditorForm'
import TimelineEditorForm from './components/TimelineEditorForm'
import DialogueEditorForm from './components/DialogueEditorForm'
import ActionEditorForm from './components/ActionEditorForm'
import EventEditorForm from './components/EventEditorForm'
import WorldEditorForm from './components/WorldEditorForm'
import AiModelEditorForm from './components/AiModelEditorForm'
import ConfigBlockToolbar from './components/ConfigBlockToolbar'

const { Search } = Input
const { Option } = Select
const { TabPane } = Tabs
const { Text, Title } = Typography
const { TextArea } = Input

// 配置类型图标组件映射
const ConfigTypeIcon: React.FC<{ type: ConfigType }> = ({ type }) => {
  const iconMap: Record<ConfigType, React.ReactNode> = {
    npc: <TeamOutlined />,
    quest: <AimOutlined />,
    item: <ShoppingCartOutlined />,
    variable: <DatabaseOutlined />,
    timeline: <ClockCircleOutlined />,
    world: <GlobalOutlined />,
    faction: <FlagOutlined />,
    location: <EnvironmentOutlined />,
    action: <ThunderboltOutlined />,
    event: <BellOutlined />,
    dialogue: <MessageOutlined />,
    ai_model: <RobotOutlined />,
    resource_image: <PictureOutlined />,
    v21_script: <FileTextOutlined />,
    v21_world: <GlobalOutlined />,
    v21_timeline: <ClockCircleOutlined />,
    v21_variables: <DatabaseOutlined />,
  }
  return <span style={{ fontSize: 16 }}>{iconMap[type] || <OrderedListOutlined />}</span>
}

// 判断是否为系统生成的V2.1配置类型
const isSystemGeneratedV21 = (type: ConfigType): boolean => {
  return type.startsWith('v21_')
}

// 配置块详情抽屉组件
const ConfigBlockDetailDrawer: React.FC<{
  visible: boolean
  block: ScriptConfigBlock | null
  onClose: () => void
  onEdit: (block: ScriptConfigBlock) => void
}> = ({ visible, block, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = useState('info')

  if (!block) return null

  return (
    <Drawer
      title={
        <Space>
          <ConfigTypeIcon type={block.config_type as ConfigType} />
          <span>{block.config_name}</span>
          <Tag color={block.is_active ? 'green' : 'red'}>
            {block.is_active ? '已激活' : '已禁用'}
          </Tag>
        </Space>
      }
      placement="right"
      width={600}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button icon={<EditOutlined />} onClick={() => onEdit(block)}>
            编辑
          </Button>
          <Button icon={<CloseOutlined />} onClick={onClose} />
        </Space>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="基本信息" key="info">
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text type="secondary">配置类型</Text>
              <div>
                <ConfigTypeIcon type={block.config_type as ConfigType} />
                <Text strong style={{ marginLeft: 8 }}>
                  {CONFIG_TYPE_LABELS[block.config_type as ConfigType] || block.config_type}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary">配置键</Text>
              <div>
                <Text copyable style={{ fontFamily: 'monospace' }}>
                  {block.config_key}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary">版本号</Text>
              <div>
                <Tag>v{block.version}</Tag>
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary">排序权重</Text>
              <div>
                <Text>{block.sort_order}</Text>
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary">创建时间</Text>
              <div>
                <Text>{new Date(block.created_at).toLocaleString('zh-CN')}</Text>
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary">更新时间</Text>
              <div>
                <Text>{new Date(block.updated_at).toLocaleString('zh-CN')}</Text>
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">配置数据 (JSON)</Text>
              <TextArea
                value={JSON.stringify(block.config_data, null, 2)}
                rows={10}
                readOnly
                style={{ marginTop: 8, fontFamily: 'monospace' }}
              />
            </Col>
          </Row>
        </TabPane>
        <TabPane tab="JSON 数据" key="json">
          <TextArea
            value={JSON.stringify(block.config_data, null, 2)}
            rows={20}
            readOnly
            style={{ fontFamily: 'monospace' }}
          />
        </TabPane>
      </Tabs>
    </Drawer>
  )
}

// 新建/编辑配置块 Drawer (单页面版)
const ConfigBlockDrawer: React.FC<{
  visible: boolean
  onCancel: () => void
  onOk: (values: any) => Promise<void>
  editingBlock: ScriptConfigBlock | null
  packageId: string
  initialConfigType?: ConfigType
}> = ({ visible, onCancel, onOk, editingBlock, packageId, initialConfigType }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [configType, setConfigType] = useState<ConfigType | undefined>(initialConfigType)
  const [editorMode, setEditorMode] = useState<'form' | 'json'>('form')
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewData, setPreviewData] = useState<any>(null)

  // 检查是否有专用编辑器
  const hasFormEditor = (type: ConfigType): boolean => {
    return [
      'npc', 'quest', 'item', 'variable', 'faction', 'location', 'timeline',
      'dialogue', 'action', 'event', 'world', 'ai_model'
    ].includes(type)
  }

  useEffect(() => {
    if (visible) {
      setEditorMode('form')
      setPreviewVisible(false)

      if (editingBlock) {
        // 编辑模式：加载现有数据
        form.setFieldsValue({
          config_type: editingBlock.config_type,
          config_key: editingBlock.config_key,
          config_name: editingBlock.config_name,
          config_data: JSON.stringify(editingBlock.config_data, null, 2),
          sort_order: editingBlock.sort_order,
        })
        setConfigType(editingBlock.config_type as ConfigType)
      } else {
        // 新建模式：设置默认值
        form.resetFields()
        const defaultType = initialConfigType || 'npc'
        form.setFieldsValue({
          config_type: defaultType,
          sort_order: 0,
        })
        setConfigType(defaultType)
      }
    }
  }, [visible, editingBlock, initialConfigType, form])

  // 根据配置类型生成默认模板
  const getDefaultTemplate = () => {
    if (!configType) return '{}'

    const templates: Record<ConfigType, object> = {
      npc: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新NPC',
        role: '神秘角色',
        personality: '沉默寡言',
        background: '背景故事...',
        system_prompt: '你是一个神秘的角色...',
      },
      quest: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新任务',
        description: '任务描述...',
        quest_type: 'main',
        objectives: [{ id: '1', description: '完成第一步', type: 'talk', target_key: '' }],
        rewards: [],
      },
      item: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新物品',
        description: '物品描述...',
        item_type: 'consumable',
        rarity: 'common',
        effects: [],
      },
      variable: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新变量',
        var_type: 'boolean',
        scope: 'global',
        initial_value: false,
      },
      timeline: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新时间线',
        events: [{ id: '1', time_offset: 0, type: 'event', content: {} }],
      },
      world: {
        key: 'world_settings',
        name: '世界观设定',
        setting: '现代都市',
        atmosphere: '悬疑',
        rules: [],
        tags: [],
      },
      faction: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新派系',
        ideology: '派系理念...',
        goals: [],
        relationships: [],
      },
      location: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新地点',
        location_type: 'indoor',
        connections: [],
        properties: {},
      },
      action: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新动作',
        type: 'dialogue',
        parameters: {},
      },
      event: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新事件',
        event_type: 'trigger',
        trigger_conditions: {},
        effects: [],
      },
      dialogue: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新对话',
        participants: [],
        dialogues: [],
      },
      ai_model: {
        key: `${configType}_${Date.now().toString().slice(-6)}`,
        name: '新AI模型',
        provider: 'doubao',
        model_id: 'doubao-seed-evolving',
        temperature: 0.7,
        max_tokens: 2000,
        system_prompt: '',
      },
    }

    return JSON.stringify(templates[configType] || {}, null, 2)
  }

  // 保存表单编辑器数据
  const handleFormSave = async (data: any) => {
    // JSON Schema 验证
    const validationResult = validateConfigData(configType!, data)
    if (!validationResult.valid) {
      const errorSummary = getValidationErrorSummary(validationResult.errors || [])
      message.error(`配置数据验证失败: ${errorSummary}`)
      return
    }

    const configKey = data.key || `${configType}_${Date.now().toString().slice(-6)}`
    const configName = data.name || `新${CONFIG_TYPE_LABELS[configType!]}`

    await onOk({
      package_id: packageId,
      config_type: configType,
      config_key: configKey,
      config_name: configName,
      config_data: data,
      sort_order: 0,
    })
  }

  // JSON 模式保存
  const handleJsonSave = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      let configData = {}
      try {
        configData = JSON.parse(values.config_data || '{}')
      } catch (e) {
        message.error('配置数据 JSON 格式错误')
        setLoading(false)
        return
      }

      // JSON Schema 验证
      const validationResult = validateConfigData(values.config_type, configData)
      if (!validationResult.valid) {
        const errorSummary = getValidationErrorSummary(validationResult.errors || [])
        message.error(`配置数据验证失败: ${errorSummary}`)
        setLoading(false)
        return
      }

      await onOk({
        package_id: packageId,
        config_type: values.config_type,
        config_key: values.config_key,
        config_name: values.config_name,
        config_data: configData,
        sort_order: values.sort_order || 0,
      })
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 渲染表单编辑器（根据配置类型）
  const renderFormEditor = () => {
    if (!configType) return null

    // 简化 props，只传递编辑器真正需要的属性
    const commonProps = {
      initialData: editingBlock?.config_data || {},
      onSave: handleFormSave,
    }

    try {
      switch (configType) {
        case 'npc': return <NpcEditorForm {...commonProps} />
        case 'quest': return <QuestEditorForm {...commonProps} />
        case 'item': return <ItemEditorForm {...commonProps} />
        case 'variable': return <VariableEditorForm {...commonProps} />
        case 'faction': return <FactionEditorForm {...commonProps} />
        case 'location': return <LocationEditorForm {...commonProps} />
        case 'timeline': return <TimelineEditorForm {...commonProps} />
        case 'dialogue': return <DialogueEditorForm {...commonProps} />
        case 'action': return <ActionEditorForm {...commonProps} />
        case 'event': return <EventEditorForm {...commonProps} />
        case 'world': return <WorldEditorForm {...commonProps} />
        case 'ai_model': return <AiModelEditorForm {...commonProps} />
        default:
          return (
            <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
              暂无专用编辑器，请切换到 JSON 模式编辑
            </div>
          )
      }
    } catch (error) {
      console.error('渲染编辑器错误:', error)
      return (
        <Alert
          message="编辑器加载失败"
          description={`无法加载 ${CONFIG_TYPE_LABELS[configType]} 编辑器，请尝试使用 JSON 模式`}
          type="error"
          showIcon
        />
      )
    }
  }

  // 单页面布局渲染
  return (
    <>
      <Drawer
        title={
          <Space>
            {configType && <ConfigTypeIcon type={configType} />}
            <span>{editingBlock ? '编辑配置块' : '新建配置块'}</span>
            {editingBlock && <Tag color="blue">编辑模式</Tag>}
          </Space>
        }
        placement="right"
        width={1000}
        open={visible}
        onClose={onCancel}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button
                type={editorMode === 'form' ? 'primary' : 'default'}
                icon={<FormOutlined />}
                onClick={() => setEditorMode('form')}
                disabled={!configType || !hasFormEditor(configType)}
              >
                表单编辑器
              </Button>
              <Button
                type={editorMode === 'json' ? 'primary' : 'default'}
                icon={<CodeOutlined />}
                onClick={() => setEditorMode('json')}
              >
                JSON 模式
              </Button>
            </Space>
            <Space>
              <Button onClick={onCancel}>取消</Button>
              {editorMode === 'json' && (
                <Button type="primary" onClick={handleJsonSave} loading={loading}>
                  保存
                </Button>
              )}
            </Space>
          </div>
        }
        footerStyle={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}
      >
        {/* 基础信息区域 */}
        <Card style={{ marginBottom: 16 }} size="small">
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="config_type"
                  label="配置类型"
                  rules={[{ required: true, message: '请选择配置类型' }]}
                >
                  <Select
                    disabled={!!editingBlock}
                    onChange={(value) => {
                      setConfigType(value as ConfigType)
                      setEditorMode(hasFormEditor(value as ConfigType) ? 'form' : 'json')
                      if (!editingBlock) {
                        form.setFieldsValue({ config_data: getDefaultTemplate() })
                      }
                    }}
                  >
                    {Object.entries(CONFIG_TYPE_LABELS)
                      .filter(([key]) => !isSystemGeneratedV21(key as ConfigType))
                      .map(([key, label]) => (
                        <Option key={key} value={key}>
                          <Space>
                            <ConfigTypeIcon type={key as ConfigType} />
                            <span>{label}</span>
                            {hasFormEditor(key as ConfigType) && <Tag color="blue" style={{ fontSize: 10 }}>表单</Tag>}
                          </Space>
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="config_key"
                  label="配置键 (Key)"
                  rules={[
                    { required: true, message: '请输入配置键' },
                    {
                      pattern: /^[a-z][a-z0-9_]*$/,
                      message: '必须以小写字母开头，只能包含小写字母、数字和下划线'
                    }
                  ]}
                >
                  <Input placeholder="例如: mysterious_stranger" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="config_name"
                  label="配置名称"
                  rules={[{ required: true, message: '请输入配置名称' }]}
                >
                  <Input placeholder="例如: 神秘陌生人" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* 详细编辑器区域 */}
        {configType && (
          <div style={{ minHeight: 500 }}>
            {editorMode === 'form' ? (
              hasFormEditor(configType) ? (
                <Card style={{ background: '#fafafa' }}>
                  <Alert
                    message={`正在使用 ${CONFIG_TYPE_LABELS[configType]} 专业编辑器`}
                    description="填写下方表单后点击保存按钮完成编辑"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {renderFormEditor()}
                  </div>
                </Card>
              ) : (
                <Alert
                  message="此配置类型暂无表单编辑器"
                  description="请切换到 JSON 模式进行编辑"
                  type="warning"
                  showIcon
                />
              )
            ) : (
              <Form form={form} layout="vertical">
                <Form.Item
                  name="config_data"
                  label={
                    <Space>
                      <span>配置数据 (JSON)</span>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          form.setFieldsValue({ config_data: getDefaultTemplate() })
                          message.success('已填入模板')
                        }}
                      >
                        填入模板
                      </Button>
                    </Space>
                  }
                  rules={[
                    { required: true, message: '请输入配置数据' },
                    {
                      validator: (_, value) => {
                        try {
                          JSON.parse(value || '{}')
                          return Promise.resolve()
                        } catch {
                          return Promise.reject(new Error('JSON 格式错误'))
                        }
                      }
                    }
                  ]}
                >
                  <TextArea rows={25} style={{ fontFamily: 'monospace' }} placeholder="{}" />
                </Form.Item>
              </Form>
            )}
          </div>
        )}
      </Drawer>

      {/* JSON 预览抽屉 */}
      <Drawer
        title="JSON 预览"
        placement="right"
        width={600}
        open={previewVisible}
        onClose={() => setPreviewVisible(false)}
        footer={
          <Button type="primary" onClick={() => {
            navigator.clipboard.writeText(JSON.stringify(previewData, null, 2))
            message.success('已复制到剪贴板')
          }}>
            复制 JSON
          </Button>
        }
      >
        <TextArea
          value={JSON.stringify(previewData, null, 2)}
          rows={30}
          style={{ fontFamily: 'monospace' }}
          readOnly
        />
      </Drawer>
    </>
  )
}

// 统计卡片组件
const StatsCards: React.FC<{ stats: ConfigBlockStatsResponse | null }> = ({ stats }) => {
  if (!stats) return null

  // 计算激活和禁用数量（如果后端没有直接返回）
  const total = stats.total || 0
  const typeCount = Object.keys(stats.stats || {}).length

  return (
    <Row gutter={16} style={{ marginBottom: 16 }}>
      <Col span={6}>
        <Card>
          <Statistic
            title="总配置数"
            value={total}
            prefix={<DatabaseOutlined />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="已激活"
            value={stats.active_count || total}
            valueStyle={{ color: '#52c41a' }}
            prefix={<Badge status="success" />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="已禁用"
            value={stats.inactive_count || 0}
            valueStyle={{ color: '#ff4d4f' }}
            prefix={<Badge status="error" />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="配置类型"
            value={typeCount}
            prefix={<OrderedListOutlined />}
          />
        </Card>
      </Col>
    </Row>
  )
}

// 剧本概览类型
interface ScriptOverview {
  id: number
  title: string
  subtitle?: string
  genre: string
  min_players: number
  max_players: number
  price: number
  status: string
  package_ids?: number[]  // 关联的 script_packages 数据库 ID 列表
}

// 主页面组件
const ConfigBlockManagement: React.FC = () => {
  // 从 URL 读取 script_id 参数（用于从剧本管理页面跳转过来）
  const [searchParams] = useSearchParams()
  const urlScriptId = parseInt(searchParams.get('script_id') || '0')

  const [blocks, setBlocks] = useState<ScriptConfigBlock[]>([])
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<ConfigBlockStatsResponse | null>(null)
  const [scripts, setScripts] = useState<ScriptOverview[]>([])
  const [selectedScriptId, setSelectedScriptId] = useState<number>(urlScriptId)
  const [selectedScriptName, setSelectedScriptName] = useState<string>('')
  const [selectedScriptPackageIds, setSelectedScriptPackageIds] = useState<number[]>([])
  const [selectedPackageIdStr, setSelectedPackageIdStr] = useState<string>('')  // 剧本包的 package_id (字符串标识符)
  const [searchText, setSearchText] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })

  // 使用 ref 存储过滤条件，避免 fetchBlocks 依赖循环
  const typeFilterRef = React.useRef(typeFilter)
  const searchTextRef = React.useRef(searchText)

  // 同步 ref 和 state
  React.useEffect(() => {
    typeFilterRef.current = typeFilter
  }, [typeFilter])

  React.useEffect(() => {
    searchTextRef.current = searchText
  }, [searchText])

  // 弹窗状态
  const [modalVisible, setModalVisible] = useState(false)
  const [editingBlock, setEditingBlock] = useState<ScriptConfigBlock | null>(null)
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  const [selectedBlock, setSelectedBlock] = useState<ScriptConfigBlock | null>(null)
  const [initialConfigType, setInitialConfigType] = useState<ConfigType | undefined>()

  // 获取剧本列表 (从 scripts 表获取，与剧本管理页面一致)
  // 同时获取每个剧本关联的 script_packages ID 列表
  // 使用 ref 避免依赖循环
  const hasInitializedRef = React.useRef(false)
  const fetchScripts = useCallback(async () => {
    try {
      const response = await scriptApi.getScripts({ page_size: 100 })
      if (response.data?.items) {
        const scriptList = response.data.items.map((s: any) => ({
          id: s.id,
          title: s.title,
          subtitle: s.subtitle,
          genre: s.genre,
          min_players: s.min_players,
          max_players: s.max_players,
          price: s.price,
          status: s.status,
          package_ids: s.package_ids || [],  // 如果后端返回了关联的 package IDs
        }))
        setScripts(scriptList)

        // 如果 URL 中有 script_id 参数，优先选中对应的剧本
        // 使用 ref 确保只初始化一次，避免重复设置
        if (urlScriptId > 0) {
          const targetScript = scriptList.find((s) => s.id === urlScriptId)
          if (targetScript && !hasInitializedRef.current) {
            hasInitializedRef.current = true
            setSelectedScriptId(targetScript.id)
            setSelectedScriptName(`[${targetScript.id}] ${targetScript.title}`)
            setSelectedScriptPackageIds(targetScript.package_ids || [])
          }
        } else if (scriptList.length > 0 && !hasInitializedRef.current) {
          // 没有URL参数，默认选中第一个剧本
          hasInitializedRef.current = true
          setSelectedScriptId(scriptList[0].id)
          setSelectedScriptName(`[${scriptList[0].id}] ${scriptList[0].title}`)
          setSelectedScriptPackageIds(scriptList[0].package_ids || [])
        }
      }
    } catch (error) {
      console.error('获取剧本列表失败:', error)
      message.error('获取剧本列表失败')
    }
  }, [urlScriptId])  // 只依赖 urlScriptId，避免 selectedScriptId 导致的循环

  // 获取配置块列表 (优先使用 script_id 查询，支持 AI 生成的配置块)
  // 使用 ref 获取最新的 filter 值，避免依赖循环
  const fetchBlocks = useCallback(async () => {
    if (!selectedScriptId) return

    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // 创建新的 AbortController
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    // 从 ref 获取最新的过滤条件，避免依赖循环
    const currentTypeFilter = typeFilterRef.current
    const currentSearchText = searchTextRef.current

    setLoading(true)
    try {
      // 1. 先获取该剧本关联的所有 script_packages（用于导出功能）
      let packageIdStr = ''
      try {
        const packagesResponse = await scriptPackageApi.getByScriptId(selectedScriptId)
        if (abortController.signal.aborted) return

        const scriptPackages = packagesResponse.data?.items || []
        const packageIds = scriptPackages.map((p: any) => p.id)
        setSelectedScriptPackageIds(packageIds)

        // 保存第一个剧本包的 package_id (字符串标识符) 用于导出
        if (scriptPackages.length > 0) {
          packageIdStr = scriptPackages[0].package_id
          setSelectedPackageIdStr(packageIdStr)
        } else {
          setSelectedPackageIdStr('')
        }
      } catch {
        // 获取剧本包失败，不影响配置块查询
        setSelectedScriptPackageIds([])
        setSelectedPackageIdStr('')
      }

      // 2. 使用 script_id 直接查询配置块（支持 AI 生成的配置块）
      const currentPage = paginationRef.current.current
      const pageSize = paginationRef.current.pageSize

      const params: any = {
        page: currentPage,
        limit: pageSize,
        script_id: selectedScriptId,  // 优先使用 script_id 查询
      }

      if (currentTypeFilter) params.config_type = currentTypeFilter
      if (currentSearchText) params.config_key = currentSearchText  // 使用 config_key 参数支持模糊搜索

      const response = await configBlockApi.getBlocks(params)
      if (abortController.signal.aborted) return

      if (response.data) {
        setBlocks(response.data.items || [])
        setPagination(prev => ({ ...prev, total: response.data.total || 0 }))
      }

      // 3. 获取统计信息
      try {
        const statsResponse = await configBlockApi.getStatsByScript(selectedScriptId)
        if (abortController.signal.aborted) return
        if (statsResponse.data) {
          setStats(statsResponse.data)
        }
      } catch {
        // 统计失败，忽略
        setStats({ script_id: selectedScriptId, stats: {}, total: response.data?.total || 0 })
      }
    } catch (error: any) {
      // 忽略由于取消导致的错误
      if (error.name === 'CancelledError' || error.code === 'ERR_CANCELLED') {
        return
      }
      console.error('获取配置块列表失败:', error)
      message.error(error?.response?.data?.message || error?.message || '获取配置块列表失败')
    } finally {
      // 只有在没有被取消的情况下才设置 loading 为 false
      if (!abortController.signal.aborted) {
        setLoading(false)
      }
    }
  }, [selectedScriptId])  // 只依赖 selectedScriptId，其他用 ref

  // 使用 ref 来存储 AbortController，防止异步竞态条件
  const abortControllerRef = React.useRef<AbortController | null>(null)

  // 组件卸载时取消未完成的请求
  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // 使用 ref 来追踪分页状态，避免 useCallback 依赖循环
  const paginationRef = React.useRef(pagination)
  React.useEffect(() => {
    paginationRef.current = pagination
  }, [pagination])

  useEffect(() => {
    fetchScripts()
    // fetchScripts 只依赖 urlScriptId，这里只需要在组件挂载时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 当 selectedScriptId 变化时获取配置块
  // 注意：fetchBlocks 使用 ref 获取最新过滤条件，避免依赖循环
  useEffect(() => {
    if (selectedScriptId) {
      fetchBlocks()
    }
    // 依赖项只保留 selectedScriptId，fetchBlocks 使用 ref 获取最新状态
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScriptId])

  // 当过滤条件变化时重新获取数据（使用 debounce 避免频繁请求）
  useEffect(() => {
    if (!selectedScriptId) return

    const timer = setTimeout(() => {
      fetchBlocks()
    }, 300) // 300ms debounce

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, searchText])

  // 创建配置块
  const handleCreate = async (values: any) => {
    try {
      await configBlockApi.createBlock(values)
      message.success('创建成功')
      setModalVisible(false)
      fetchBlocks()
    } catch (error: any) {
      console.error('创建失败:', error)
      message.error(error?.message || '创建失败')
    }
  }

  // 更新配置块
  const handleUpdate = async (values: any) => {
    if (!editingBlock) return

    try {
      await configBlockApi.updateBlock(editingBlock.id, values)
      message.success('更新成功')
      setModalVisible(false)
      setEditingBlock(null)
      fetchBlocks()
    } catch (error: any) {
      console.error('更新失败:', error)
      message.error(error?.message || '更新失败')
    }
  }

  // 删除配置块
  const handleDelete = async (blockId: number) => {
    try {
      await configBlockApi.deleteBlock(blockId)
      message.success('删除成功')
      fetchBlocks()
    } catch (error: any) {
      console.error('删除失败:', error)
      message.error(error?.message || '删除失败')
    }
  }

  // 表格列定义
  const columns: ColumnsType<ScriptConfigBlock> = [
    {
      title: '配置键',
      dataIndex: 'config_key',
      key: 'config_key',
      width: 180,
      render: (text: string) => (
        <Text code style={{ fontFamily: 'monospace' }}>{text}</Text>
      ),
    },
    {
      title: '配置名称',
      dataIndex: 'config_name',
      key: 'config_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'config_type',
      key: 'config_type',
      width: 140,
      render: (type: ConfigType) => (
        <Space direction="vertical" size={0}>
          <Tag icon={<ConfigTypeIcon type={type} />}>
            {CONFIG_TYPE_LABELS[type] || type}
          </Tag>
          {isSystemGeneratedV21(type) && (
            <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>系统生成</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      render: (v: number) => <Tag>v{v}</Tag>,
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: boolean) => (
        <Badge status={active ? 'success' : 'error'} text={active ? '已激活' : '已禁用'} />
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
      sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 180,
      render: (_, record: ScriptConfigBlock) => {
        const isSystemV21 = isSystemGeneratedV21(record.config_type)
        return (
          <Space size="small">
            <Tooltip title="查看详情">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedBlock(record)
                  setDetailDrawerVisible(true)
                }}
              />
            </Tooltip>
            {isSystemV21 ? (
              <Tooltip title="系统生成的配置，不可编辑">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  disabled
                />
              </Tooltip>
            ) : (
              <Tooltip title="编辑">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingBlock(record)
                    setInitialConfigType(undefined)
                    setModalVisible(true)
                  }}
                />
              </Tooltip>
            )}
            {isSystemV21 ? (
              <Tooltip title="系统生成的配置，不可删除">
                <Button type="text" danger icon={<DeleteOutlined />} disabled />
              </Tooltip>
            ) : (
              <Popconfirm
                title="确认删除"
                description="确定要删除此配置块吗？此操作不可恢复。"
                onConfirm={() => handleDelete(record.id)}
                okText="确认"
                cancelText="取消"
              >
                <Tooltip title="删除">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>剧本配置块管理</Title>

      {/* 顶部筛选栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space>
              <Select
                placeholder="选择剧本"
                value={selectedScriptId || undefined}
                onChange={(scriptId) => {
                  setSelectedScriptId(scriptId || 0)
                  const script = scripts.find(s => s.id === scriptId)
                  setSelectedScriptName(script ? `[${script.id}] ${script.title}` : '')
                  setSelectedScriptPackageIds(script?.package_ids || [])
                  setSelectedPackageIdStr('')  // 重置，将在 fetchBlocks 中更新
                  // 切换剧本时重置分页和搜索条件
                  setPagination(prev => ({ ...prev, current: 1 }))
                  setSearchText('')
                  setTypeFilter('')
                }}
                style={{ width: 250 }}
                allowClear
              >
                {scripts.map((script) => (
                  <Option key={script.id} value={script.id}>
                    [{script.id}] {script.title}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="配置类型"
                value={typeFilter || undefined}
                onChange={setTypeFilter}
                style={{ width: 150 }}
                allowClear
              >
                {Object.entries(CONFIG_TYPE_LABELS).map(([key, label]) => (
                  <Option key={key} value={key}>
                    <Space>
                      <ConfigTypeIcon type={key as ConfigType} />
                      {label}
                    </Space>
                  </Option>
                ))}
              </Select>
              <Search
                placeholder="搜索配置键"
                allowClear
                style={{ width: 200 }}
                onSearch={setSearchText}
                onChange={(e) => !e.target.value && setSearchText('')}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchBlocks}>
                刷新
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingBlock(null)
                  setInitialConfigType(undefined)
                  setModalVisible(true)
                }}
                disabled={!selectedScriptId}
              >
                新建配置块
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 导入导出工具栏 */}
      {selectedScriptId && selectedPackageIdStr && (
        <div style={{ marginBottom: 16 }}>
          <ConfigBlockToolbar
            packageId={selectedPackageIdStr}
            packageName={selectedScriptName}
            onRefresh={fetchBlocks}
          />
        </div>
      )}

      {/* 统计卡片 */}
      {!loading && stats && <StatsCards stats={stats} />}

      {/* 配置块列表 */}
      <Card>
        {selectedScriptId ? (
          <Table
            columns={columns}
            dataSource={blocks}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }))
                fetchBlocks()  // ✅ 重新获取数据
              },
            }}
            scroll={{ x: 1200 }}
          />
        ) : (
          <Empty description="请先选择剧本" />
        )}
      </Card>

      {/* 新建/编辑 Drawer */}
      {modalVisible && (
        <ConfigBlockDrawer
          visible={modalVisible}
          onCancel={() => {
            setModalVisible(false)
            setEditingBlock(null)
          }}
          onOk={editingBlock ? handleUpdate : handleCreate}
          editingBlock={editingBlock}
          packageId={selectedScriptId.toString()}
          initialConfigType={initialConfigType}
        />
      )}

      {/* 详情抽屉 */}
      {selectedBlock && (
        <ConfigBlockDetailDrawer
          visible={detailDrawerVisible}
          block={selectedBlock}
          onClose={() => {
            setDetailDrawerVisible(false)
            setSelectedBlock(null)
          }}
          onEdit={(block) => {
            setDetailDrawerVisible(false)
            setEditingBlock(block)
            setModalVisible(true)
          }}
        />
      )}
    </div>
  )
}

export default ConfigBlockManagement
